import { CMSModule, ModuleBase } from '@/components';
import { ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import classNames from 'classnames';

export enum Layout {
  ONE_BY_TWO = '1*,2*',
  TWO_BY_ONE = '2*,1*',
}

type ColumnLayoutModel = ModuleBaseModel<
  {
    layout: Layout;
    columns: ModuleBaseModel<ModuleBasePropsModel>[];
  } & ModuleBasePropsModel
>;

const classOverride = classNames(
  '[&_.ReportsGridModule_.flicking-viewport]:!px-[var(--container-padding)] [&_.ReportsGridModule_.flicking-viewport]:md:px-[var(--container-padding-md)]',
  '[&_.ReportsGridModule_.report-card_.title_div]:md:line-clamp-1 [&_.ReportsGridModule_.report-card_.title_div]:xxl:line-clamp-2',
  '[&_.ReportsGridModule_.report-card-container]:overflow-hidden',

  '[&_.ReportsGridModule_.report-listing-grid-heading]:lg:pt-6',
  '[&_.ReportsGridModule_.report-listing-grid-heading]:lg:pr-6 [&_.ReportsGridModule_.report-listing-grid-heading]:lg:pl-2',
  '[&_.ReportsGridModule_.report-listing-grid-container]:lg:pr-6 [&_.ReportsGridModule_.report-listing-grid-container]:lg:pl-2',
  '[&_.ReportsGridModule_.report-listing-grid-heading]:xl:pr-10 [&_.ReportsGridModule_.report-listing-grid-heading]:xl:pl-6',
  '[&_.ReportsGridModule_.report-listing-grid-container]:xl:pr-10 [&_.ReportsGridModule_.report-listing-grid-container]:xl:pl-6',

  '[&_.RichTextModule_.richText]:!px-0 [&_.RichTextModule_.richText]:!mx-0 [&_.RichTextModule_.richText]:!w-full',
  '[&_.InnovationPlatformsModule]:lg:px-6 [&_.InnovationPlatformsModule]:xl:px-10',
  '[&_.InnovationPlatformsModule_.ip-list-item]:!flex',
  '[&_.InnovationPlatformsModule_.ip-list-item]:!flex-col [&_.InnovationPlatformsModule_.ip-list-item]:!items-center',
  '[&_.InnovationPlatformsModule_.ip-list-item]:justify-center',
  '[&_.InnovationPlatformsModule_.text-container]:lg:font-light [&_.InnovationPlatformsModule_.text-container]:lg:text-xl',
  '[&_.InnovationPlatformsModule_.text-container]:lg:w-10/12 [&_.InnovationPlatformsModule_.text-container]:xl:w-10/12',
  '[&_.InnovationPlatformsModule_.container]:md:py-0 [&_.InnovationPlatformsModule_.container]:md:h-full',

  '[&_.InnovationPlatformsModule_.list-container]:flex-wrap [&_.InnovationPlatformsModule_.list-container]:md:h-full',
  '[&_.InnovationPlatformsModule_.category-name-container]:text-2xl [&_.InnovationPlatformsModule_.category-name-container]:min-h-[55px]',
  '[&_.InnovationPlatformsModule_.container.gap-6]:gap-3 [&_.InnovationPlatformsModule_.image-container]:group-hover:scale-100',
  '[&_.InnovationPlatformsModule_h2]:lg:text-4xl [&_.InnovationPlatformsModule_h2]:text-4xl [&_.InnovationPlatformsModule_h2]:md:text-[22px] [&_.InnovationPlatformsModule_h2]:lg:leading-[36px]',
  '[&_.InnovationPlatformsModule]:h-full',

  '[&_.PollingModule_.container]:px-0',
  '[&_.PollingModule_.polling-content]:grow',
  '[&_.PollingModule]:h-full',

  '[&_.TopTenModule_.container]:px-0',
  '[&_.TopTenModule_.top-ten-content]:grow',
  '[&_.TopTenModule]:h-full',
  '[&_.TopTenModule]:!py-0',
  '[&_.ColumnLayout]:!py-0',
);

const ColumnLayout = (data: ColumnLayoutModel) => {
  const {
    props: { layout, columns },
  } = data;

  const areThereTwoPoles =
    layout === Layout.ONE_BY_TWO &&
    columns.length === 2 &&
    columns[0].moduleName === 'PollingModule' &&
    // @ts-expect-error: Unreachable code error
    columns[0].props?.polls?.length === 2;

  return (
    <ModuleBase data={data} noModuleBaseColor>
      <div
        className={classNames(
          'mx-auto flex max-w-[var(--breakpoint-xxl)] flex-col flex-wrap md:flex-row',
          areThereTwoPoles
            ? '[&_.InnovationPlatformsModule_.ip-list-item]:!w-1/2 [&_.InnovationPlatformsModule_.ip-list-item]:xl:!w-1/3'
            : '[&_.InnovationPlatformsModule_.ip-list-item]:!w-1/3',
          classOverride,
        )}
      >
        {columns.length > 0 &&
          columns.map((column, index) => (
            <ColumnLayoutContent key={column.id} data={column} index={index} layout={layout} />
          ))}
      </div>
    </ModuleBase>
  );
};

const ColumnLayoutContent = ({
  data,
  index,
  layout,
}: {
  data: ModuleBaseModel<ModuleBasePropsModel>;
  index: number;
  layout: Layout;
}) => {
  let layoutClasses = 'w-full pb-8 lg:pb-0';

  if (layout === Layout.ONE_BY_TWO) {
    const row = Math.floor(index / 2);
    const isOddRow = row % 2 === 1;
    const isEvenColumn = index % 2 === 0;
    layoutClasses = classNames(
      layoutClasses,
      (isOddRow && !isEvenColumn) || (!isOddRow && isEvenColumn) ? 'md:w-1/3 md:pr-2' : 'md:w-2/3 md:pl-2',
    );
  } else if (layout === Layout.TWO_BY_ONE) {
    const row = Math.floor(index / 2);
    const isEvenRow = row % 2 === 0;
    const isOddColumn = index % 2 === 1;
    layoutClasses = classNames(
      layoutClasses,
      (isEvenRow && !isOddColumn) || (!isEvenRow && isOddColumn) ? 'md:w-2/3 md:pr-2' : 'md:w-1/3 md:pl-2',
    );
  }

  return (
    <div className={layoutClasses}>
      <CMSModule module={data} />
    </div>
  );
};

export default ColumnLayout;
